import type { DialogProps } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TValueForService } from "../../../../../services/api/instances/storageApi/types/service_types";
import { useAppDispatch } from "../../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../../services/redux/hooks/use-selector";
import { selectAssistantValue } from "../../../redux/selectors";
import { useAssistantField } from "../../../hooks/use-assistant-field";
import { generatePromptAction } from "../../../redux/actions";
import { getUpdatedValue } from "../../../helpers/getUpdatedValue";
import { ModalBasic } from "../../../../../shared/components/ModalBasic";
import { GeneratePromptBody } from "./GeneratePromptBody";
import { getInstructionWithoutHiddenPart } from "../../../helpers/getInstructionWithoutHiddenPart";
import { TModalsIds } from "../../../../../app/state/modalSlice/types";
import { rebuildSecurityBlocks } from "../../../helpers/rebuildSecurityBlocks";
import { useModalSelectors } from "../../../../../app/state/modalSlice/hook/use-modal-selectors";

interface Props extends Omit<DialogProps, "open"> {
  saveAssistant: (value: TValueForService<"assistants">) => void;
  closeModal: (id: TModalsIds) => void;
}

export const GeneratePromptModal = ({
  saveAssistant,
  closeModal,
  ...props
}: Props) => {
  const dispatch = useAppDispatch();
  const assistantValue = useAppSelector(selectAssistantValue);
  const instruction = useAssistantField("instructions");
  const [result, setResult] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const generatePrompt = useModalSelectors("generate_prompt_modal");
  const { t } = useTranslation();

  const handleGeneratePrompt = () => {
    setIsLoading(true);
    dispatch(generatePromptAction())
      .unwrap()
      .then((response) => setResult(response?.system_prompt ?? ""))
      .finally(() => setIsLoading(false));
  };

  const updateAssistantInstruction = () => {
    if (!assistantValue?.assistant_object) return;
    const finalInstruction = rebuildSecurityBlocks(
      result,
      assistantValue?.meta?.tools?.assistants ?? null,
      assistantValue?.meta?.tools?.skills ?? null,
    );
    const newValue = getUpdatedValue(assistantValue, {
      instructions: finalInstruction,
    });
    saveAssistant(newValue);
  };

  return (
    <ModalBasic
      open={generatePrompt.isOpen}
      title={t("assistants.generate_prompt_title")}
      hasCloseButton
      hasFooter
      onOk={() => {
        updateAssistantInstruction();
        setResult("");
        closeModal("generate_prompt_modal");
      }}
      buttonOkText={t("assistants.generate_prompt_ok_button")}
      disabledOkBtn={result === ""}
      onClose={() => closeModal("generate_prompt_modal")}
      closeModal={() => closeModal("generate_prompt_modal")}
      onCancel={() => {
        setResult("");
        closeModal("generate_prompt_modal");
      }}
      {...props}
    >
      <GeneratePromptBody
        initialPrompt={instruction ?? ""}
        result={getInstructionWithoutHiddenPart(result)}
        isLoading={isLoading}
        generatePrompt={handleGeneratePrompt}
      />
    </ModalBasic>
  );
};
